import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Grid,
  FormLabel,
  TextField,
  Checkbox,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  ThemeProvider,
  createTheme,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from "@mui/material";
import { CommonButton } from "../common/commonButton";
import instance from "../../redux/api";
import { getAllPaymentIds, getPayoutSummary } from "../../Services/api";
import { numberFormatter } from '../../utils/generalFunctions'
import "./bdx.css";
import MUIDataTable from "mui-datatables";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import { format } from 'date-fns';
import FilterListIcon from '@mui/icons-material/FilterList';
import { commonToast, CommonFilterSidebar, CommonPaginationContainer } from "../common";
import {
  primaryButtonStyle,
  primaryButtonHoverColor,
} from "../common/commonButtonStyles";
import { useStyles } from "./paymentUpdatesContainerStyles";
import { getPaginationCount } from "../CashAllocation/utils";

function handleAmount(val) {
  const options = {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  return (
    (Number(val) && Number(val).toLocaleString("en-US", options)) ||
    (val === 0 && parseInt(val).toFixed(2)) ||
    val
  );
}

const CoversheetList = props => {
  const { search } = useLocation();
  const coverholderName = new URLSearchParams(search).get("coverholder_name");
  const settlementCurrency = new URLSearchParams(search).get("settlementCurrency");
  const paymentType = new URLSearchParams(search).get("paymentType");
  const classes = useStyles();

  const [paymentIds, setPaymentIds] = useState([]);
  const [isSearchPerformed, setIsSearchPerformed] = useState(false);
  const [isClearPerformed, setIsClearPerformed] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [transformedData, setTransformedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(true);
  const [paginationCount, setPaginationCount] = useState(0);

  const [searchFieldParams, setSearchFieldsParams] = useState({
    type_of_payment: paymentType ? [paymentType] : [],
    coverholder_name: coverholderName || "",
    settlement_currency: settlementCurrency ? [settlementCurrency] : [],
    payment_id: [],
  });

  const getCurrencies = async () => {
    instance
      .get(`documents/currency_details/`)
      .then((response) => setCurrencies(response?.data ?? []))
      .catch((error) =>
        console.error("Error fetching dropdown values:", error)
      );
  }

  const [pageState, setPageState] = useState({
    isLoading: false,
    data: [],
    total: 0,
    page: 1,
    pageSize: 25,
  });

  // Calculate start and end indices for display
  const totalEntries = pageState.total > 0 ? pageState.total : pageState.data.length;

const startRecord = (pageState.page - 1) * pageState.pageSize + 1;
const endRecord = Math.min(pageState.page * pageState.pageSize, totalEntries);
const hasData = totalEntries > 0;

  // Layout Container Style matching PremiumBDX
  const Container = {
  width: "100%",
  display: "flex",
  flexDirection: "row",
  height: "calc(100vh - 180px)",
  overflow: "hidden",
  backgroundColor: "#fafafa",
};

  const paymentTypes = [
    "Syndicate Payment",
    "Internal Broking",
    "MGA Commission",
    "Rebate",
  ];

  const paymentStatus = [
    { label: "Paid", value: 'paid' },
    { label: "Not Paid", value: 'unpaid' },
    { label: "Pending", value: 'pending' }
  ];

  const getPaymentIds = async () => {
    const response = await instance.get(`${getAllPaymentIds}`);
    setPaymentIds(response?.data?.data);
  };

  const searchQueryParams = () => {
    const { type_of_payment, coverholder_name, settlement_currency, payment_id } = searchFieldParams;
    let queryParams = [];

    if (type_of_payment && type_of_payment.length > 0) {
      queryParams.push(`type_of_payment=${type_of_payment[0]}`);
    }
    if (coverholder_name) queryParams.push(`coverholder_name=${coverholder_name}`);

    if (settlement_currency && settlement_currency.length > 0) {
      queryParams.push(`settlement_currency=${settlement_currency[0]}`);
    }

    if (payment_id && payment_id.length > 0) {
      queryParams.push(`payment_id=${payment_id[0]}`);
    }

    return queryParams.length ? `&${queryParams.join('&')}` : '';
  };

  const [selectedRecord, setSelectedRecord] = useState(null);

  const columns = [
    {
      field: "id",
      name: "ID",
      options: {
        display: false,
      },
    },
    {
      name: "Select",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => {
          const rowId = tableMeta.rowData[0];
          const isChecked = selectedRecord === rowId;
          const status = tableMeta.rowData[13];
          return (
            <div style={{ textAlign: 'center' }}>
              <Checkbox
                checked={isChecked}
                onChange={(e) => handleCheckboxChange(e.target.checked, rowId)}
                sx={{
                  color: status === "paid" ? "gray" : "#1672DE",
                  '&.Mui-checked': {
                    color: "#1672DE",
                  },
                }}
                disabled={status === "paid"}
              />
            </div>
          );
        },
      },
    },
    {
      field: "payment_id",
      name: "Payment ID",
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'center' }}>{value}</div>
        ),
      },
    },
    {
      field: "payment_type",
      name: "Payment Type",
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'center' }}>{value}</div>
        ),
      },
    },
    {
      field: "coverholder_name",
      name: "Coverholder Name",
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'center' }}>{value}</div>
        ),
      },
    },
    {
      field: "final_bank_account",
      name: "Final Bank Account",
    },
    {
      field: "final_bank_name",
      name: "Final Bank Name",
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'center' }}>{value}</div>
        ),
      },
    },
    {
      field: "to_bank_account_name",
      name: "To Bank Account Name",
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'center' }}>{value}</div>
        ),
      },
    },
    {
      field: "to_bank_account",
      name: "To Bank Account",
    },
    {
      field: "settlement_currency",
      name: "Settlement Ccy",
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'center' }}>{value}</div>
        ),
      },
    },
    {
      field: "sum_of_rebate",
      name: "Rebate",
      valueFormatter: (params) => {
        return numberFormatter(params.value);
      },
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'right' }}>{value}</div>
        ),
      },
    },
    {
      field: "sum_of_net_payment",
      name: `Total: ${pageState?.data.reduce((acc, row) => acc + parseFloat(row.sum_of_net_payment || 0), 0).toLocaleString()}`,
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'right' }}>{handleAmount(value)}</div>
        ),
      },
    },
    {
      field: "payment_date",
      name: "Payment Date",
      options: {
        customBodyRender: (value) => {
          const formattedDate = value ? format(new Date(value), 'dd-MM-yyyy') : '';
          return <div style={{ textAlign: 'center' }}>{formattedDate}</div>;
        },
      },
    },
    {
      field: "payment_complete_status",
      name: "Payment Status",
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'center' }}>{value}</div>
        ),
      },
    },
    {
      field: "wire_ref",
      name: "Wire #",
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'center' }}>{value}</div>
        ),
      },
    },
  ];

  const loadData = async (page = 1, pageSize = 25, isPageSizeChange = false) => {
    setLoading(true);
    setPageState((old) => ({
      ...old,
      isLoading: true,
    }));
    try {
      const queryParams = `skip=${(page - 1) * pageSize}&pageSize=${pageSize}&payment_status=true`;
      let response;
      if (isSearchPerformed) {
        const getSearchQueryParam = searchQueryParams();
        response = await instance.get(`${getPayoutSummary}?${queryParams}${getSearchQueryParam}`);
      } else {
        response = await instance.get(`${getPayoutSummary}?${queryParams}`);
      }

      const totalCount = response?.data?.total || 0;

      setPageState((old) => ({
        ...old,
        isLoading: false,
        data: response?.data?.data,
        total: totalCount,
        page: page,
        pageSize: pageSize,
      }));

      const transformedData = response?.data?.data.map(item =>
        columns.map(column => {
          if (column.field) return item[column.field] !== undefined ? item[column.field] : null;
          return null;
        })
      );
      setTransformedData(transformedData);
      setPaginationCount(getPaginationCount(totalCount, pageSize));
    } catch (err) {
      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));
      console.error("err", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pageState.total) {
      setPaginationCount(getPaginationCount(pageState.total, pageState.pageSize));
    }
  }, [pageState.total, pageState.pageSize]);

  const handleClearSearchList = async () => {
    setIsClearPerformed(true);
    setIsSearchPerformed(false);

    const clearedFields = {
      type_of_payment: [],
      coverholder_name: "",
      settlement_currency: [],
      payment_id: [],
    };
    setSearchFieldsParams(clearedFields);
  };

  const [openReleaseModal, setOpenReleaseModal] = useState(false);
  const [releasePaymentData, setReleasePaymentData] = useState({
    payment_complete_status: '',
    wire_ref: '',
    payment_date: ''
  });
  const [isButtonEnable, setIsButtonEnable] = useState(false);

  const handleReleasePaymentClick = () => {
    setOpenReleaseModal(true);
  };

  const handleCloseReleaseModal = () => {
    setOpenReleaseModal(false);
    setReleasePaymentData({
      payment_complete_status: '',
      wire_ref: '',
      payment_date: ''
    });
  };

  useEffect(() => {
    setIsButtonEnable(releasePaymentData.payment_complete_status === "" || releasePaymentData.wire_ref === "" || releasePaymentData.payment_date === "")
  }, [releasePaymentData])

  const handleReleasePaymentSubmit = async () => {
    try {
      const payloadData = {
        ...releasePaymentData,
        payment_complete_status: releasePaymentData.payment_complete_status?.label || releasePaymentData.payment_complete_status
      };

      const response = await instance.patch(
        `${getPayoutSummary}${selectedRecord}/`,
        payloadData
      );

      if (response) {
        commonToast("success", 'Payment released successfully!');
        handleCloseReleaseModal();
        loadData(); // Refresh the grid
      }
    } catch (err) {
      console.error(err);
      commonToast("error", 'Unable to release payment');
    }
  };


  const handleCheckboxChange = (checked, rowId) => {
    if (checked) {
      setSelectedRecord(rowId);
    } else {
      setSelectedRecord(null);
    }
  };
  // Add this to listen for page changes from the pagination container
useEffect(() => {
  // We only want to trigger this when the page number specifically changes
  // and it's not the initial load (handled by your useRef)
  if (!initialLoad.current) {
    loadData(pageState.page, pageState.pageSize);
  }
}, [pageState.page]);
useEffect(() => {
  if (pageState.total !== undefined) {
    setPaginationCount(getPaginationCount(pageState.total, pageState.pageSize));
  }
}, [pageState.total, pageState.pageSize]);

  const renderExpandableRow = (rowData, rowMeta) => {
    const rowId = rowData[0]; // ID is at index 0
    const originalRow = pageState.data.find(row => row.id === rowId);
    const bdxIds = originalRow?.bdx_ids || [];

    return (
      <TableRow>
        <TableCell colSpan={columns.length + 1} style={{ padding: '20px', backgroundColor: '#fafafa' }}>
          <Paper elevation={0} style={{ padding: '10px', backgroundColor: 'transparent' }}>
            <Typography variant="subtitle2" style={{ marginBottom: '10px', fontWeight: 'bold', color: '#1672DE' }}>
              Payment Details
            </Typography>
            <TableContainer component={Paper} elevation={1} style={{ borderRadius: '4px', border: '1px solid #e0e0e0' }}>
              <Table aria-label="subtable" size="small">
                <TableHead>
                  <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell style={{ fontWeight: 'bold' }}>ID</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Policy No</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Final Net Premium SC</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Rebate</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Net Payment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bdxIds.map((data, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{data.id}</TableCell>
                      <TableCell>{data.policy_no}</TableCell>
                      <TableCell>{data.finalnetpremiumsc}</TableCell>
                      <TableCell>{data.rebate}</TableCell>
                      <TableCell>{data.net_payment}</TableCell>
                    </TableRow>
                  ))}
                  {bdxIds.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No details available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </TableCell>
      </TableRow>
    );
  };

  const options = {
    filter: false,
    selectableRows: "none",
    filterType: "dropdown",
    responsive: "standard",
    rowsPerPage: pageState.pageSize,
    count: pageState.total,
    page: pageState.page - 1,
    expandableRows: true, // IMPORTANT: Must be true
    expandableRowsHeader: false,
    download: false,
    print: false,
    search: false,
    viewColumns: false,
    fixedHeader: false,
    serverSide: true,
    selectToolbarPlacement: 'none',
    onTableChange: (action, tableState) => {
      switch (action) {
        case 'changePage':
          loadData(tableState.page + 1, pageState.pageSize);
          break;
        case 'changeRowsPerPage':
          loadData(1, tableState.rowsPerPage, true);
          break;
        default:
          break;
      }
    },
    renderExpandableRow: (rowData, rowMeta) => renderExpandableRow(rowData, rowMeta),
    rowsPerPageOptions: [10, 25, 50, 100],
    textLabels: {
      body: {
        noMatch: pageState.isLoading ? <CircularProgress size={24} /> : "Sorry, no data found",
      },
    },
    elevation: 0,
    customFooter: () => null, // Hide default footer
  };


  const initialLoad = useRef(true);

  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false;
      loadData();
      getPaymentIds();
      getCurrencies();
    }
  }, [pageState?.pageSize, pageState?.total, pageState?.page]);

  useEffect(() => {
    if (isSearchPerformed) {
      loadData();
    }
  }, [isSearchPerformed]);

  useEffect(() => {
    if (isClearPerformed) {
      loadData();
      setIsClearPerformed(false);
    }
  }, [isSearchPerformed, searchFieldParams]);

  const handleEnter = (e) => {
    if (e.code === "Enter") {
      setIsSearchPerformed(true);
      loadData();
    }
  };

  const isFilterBtnDisable = () => {
    const { type_of_payment, coverholder_name, settlement_currency, payment_id } = searchFieldParams;
    return !(
      (type_of_payment && type_of_payment.length > 0) ||
      coverholder_name ||
      (settlement_currency && settlement_currency.length > 0) ||
      (payment_id && payment_id.length > 0)
    );
  };

  const filterConfigs = [
    {
      type: "bankName",
      label: "Payment Type",
      placeholder: "Search Payment Type",
      items: paymentTypes,
      selectedItems: searchFieldParams.type_of_payment,
      searchValue: "",
      onSearchChange: () => { },
      onItemChange: (value) => {
        const current = searchFieldParams.type_of_payment;
        if (current.includes(value)) {
          setSearchFieldsParams(prev => ({ ...prev, type_of_payment: current.filter(item => item !== value) }));
        } else {
          setSearchFieldsParams(prev => ({ ...prev, type_of_payment: [...current, value] }));
        }
      },
      onClear: () => setSearchFieldsParams(prev => ({ ...prev, type_of_payment: [] })),
    },
    {
      type: "textInput",
      label: "Coverholder Name",
      placeholder: "Enter Coverholder Name",
      value: searchFieldParams.coverholder_name,
      onChange: (value) => setSearchFieldsParams(prev => ({ ...prev, coverholder_name: value })),
      onKeyUp: handleEnter,
    },
    {
      type: "bankName",
      label: "Settlement Currency",
      placeholder: "Search Currency",
      items: currencies?.map(c => c.currency_code) || [],
      selectedItems: searchFieldParams.settlement_currency,
      searchValue: "",
      onSearchChange: () => { },
      onItemChange: (value) => {
        const current = searchFieldParams.settlement_currency;
        if (current.includes(value)) {
          setSearchFieldsParams(prev => ({ ...prev, settlement_currency: current.filter(item => item !== value) }));
        } else {
          setSearchFieldsParams(prev => ({ ...prev, settlement_currency: [...current, value] }));
        }
      },
      onClear: () => setSearchFieldsParams(prev => ({ ...prev, settlement_currency: [] })),
    },
    {
      type: "bankName",
      label: "Payment ID",
      placeholder: "Search Payment ID",
      items: paymentIds?.map(p => p.toString()) || [],
      selectedItems: searchFieldParams.payment_id,
      searchValue: "",
      onSearchChange: () => { },
      onItemChange: (value) => {
        const current = searchFieldParams.payment_id;
        if (current.includes(value)) {
          setSearchFieldsParams(prev => ({ ...prev, payment_id: current.filter(item => item !== value) }));
        } else {
          setSearchFieldsParams(prev => ({ ...prev, payment_id: [...current, value] }));
        }
      },
      onClear: () => setSearchFieldsParams(prev => ({ ...prev, payment_id: [] })),
    },
  ];

  // Specific Theme Overrides for MUIDataTable
  const getMuiTheme = () => createTheme({
  components: {
    MuiTableCell: {
      styleOverrides: {
        head: {
          color: "#1672DE !important", // Blue headers to match Premium BDX
          fontWeight: "600 !important",
          backgroundColor: "#f8f9fa !important",
          whiteSpace: "nowrap", // Prevents headers from wrapping
        },
        root: {
          padding: "12px 16px",
        },
      },
    },
    MUIDataTable: {
      styleOverrides: {
        paper: {
          boxShadow: "none !important",
        },
        responsiveBase: {
          overflowX: "auto !important", // REQUIRED for horizontal scroll
          overflowY: "auto !important",
          height: "calc(100vh - 380px) !important", // Adjusting height to prevent double scrollbars
        },
      }
    },
    // This part specifically targets the expand column to make it not sticky
    MUIDataTableBodyCell: {
      styleOverrides: {
        stackedParent: {
          "@media (max-width: 959.95px)": {
            display: "table-cell !important",
          }
        },
        root: {
          // Ensure the expand icon cell doesn't stay fixed
          position: "static !important", 
        }
      }
    }
  },
});

  return (
    <>
      <div style={Container}>
        <CommonFilterSidebar
          isOpen={filterSidebarOpen}
          filterConfigs={filterConfigs}
          onApplyFilters={() => {
            setIsSearchPerformed(true);
            loadData();
          }}
          onClearFilters={handleClearSearchList}
          isLoading={loading}
          isFilterDisabled={!isFilterBtnDisable()}
        />

        <Box className="main-content-area" sx={{
          flex: 1,           // Forces it to take all remaining horizontal space
  display: "flex",
  flexDirection: "column",
  minWidth: 0,       // Prevents flex items from overflowing
  backgroundColor: "#fafafa",
  overflow: "hidden"
        }}>
          {/* Header matching PremiumBDX exactly */}
          <Box className="page-header-container">
            <Typography variant="h5" className="page-heading">
              Payout Summary
            </Typography>
            <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <CommonButton
                text="Payment Confirm"
                handleClick={handleReleasePaymentClick}
                sx={primaryButtonStyle}
                hoverColor={primaryButtonHoverColor}
                disabled={!selectedRecord || loading}
              />
              <IconButton
                onClick={() => setFilterSidebarOpen(!filterSidebarOpen)}
                className="filter-toggle-button"
              >
                <FilterListIcon />
              </IconButton>
            </Box>
          </Box>

          <Box
  sx={{
    flex: 1,          // This makes the table area fill the vertical space
    display: "flex",
    flexDirection: "column",
    position: "relative", // Required for the absolute footer to stay inside
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
    margin: "0 20px 20px 20px", // Adds spacing to match Premium BDX
    overflow: "hidden",
    border: "1px solid #e8eaf0",
  }}
>
  <ThemeProvider theme={getMuiTheme()}>
    <Box sx={{ 
      flex: 1, 
      display: "flex", 
      flexDirection: "column", 
      overflow: "hidden",
      width: '100%' // Ensure ThemeProvider child is full width
    }}>
      <MUIDataTable
        data={transformedData}
        columns={columns}
        options={{
          ...options,
          responsive: "standard", // Ensures horizontal scroll works on small screens
          tableBodyHeight: 'calc(100vh - 350px)', // Adjust this to fit your screen
        }}
      />
    </Box>
  </ThemeProvider>

  {/* FIXED FOOTER STACK */}
  {/* FIXED FOOTER STACK */}
{/* FIXED FOOTER STACK */}
<Stack
  direction="row"
  alignItems="center"
  sx={{
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    backgroundColor: "#f8f9fa",
    padding: "8px 24px",
    borderTop: "1px solid #e0e0e0",
    height: "52px",
  }}
>
  <Box sx={{ flex: 1 }} />

  <Box
    sx={{
      flex: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 4,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="body2" sx={{ fontSize: "13px", color: "#666" }}>
        Rows per page:
      </Typography>
     <Select
  value={pageState.pageSize}
  // This explicitly updates the state AND reloads the data from page 1
  onChange={(e) => {
    const newSize = e.target.value;
    setPageState(old => ({ ...old, pageSize: newSize, page: 1 }));
    loadData(1, newSize); 
  }} 
  variant="standard"
  disableUnderline
  sx={{ 
    fontSize: "13px", 
    color: "#333", 
    fontWeight: 600,
    cursor: "pointer",
    "& .MuiSelect-select": {
      paddingRight: "24px !important",
    }
  }}
>
  {[10, 25, 50, 100].map((size) => (
    <MenuItem key={size} value={size}>{size}</MenuItem>
  ))}
</Select>
    </Box>

    <Typography variant="body2" sx={{ fontSize: "13px", color: "#666" }}>
  Showing {hasData ? startRecord : 0}–{hasData ? endRecord : 0} of {totalEntries} entries
</Typography>
  </Box>

  <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
    <CommonPaginationContainer
      count={paginationCount}
      pageState={pageState}
      setPageState={setPageState}
      // Change: Ensure the pagination container triggers a data reload when arrows are clicked
      setShouldLoadData={(shouldLoad) => {
        if (shouldLoad) {
          loadData(pageState.page, pageState.pageSize);
        }
      }}
    />
  </Box>
</Stack>
</Box>
        </Box>
      </div>

      <Dialog open={openReleaseModal} onClose={handleCloseReleaseModal}>
        <DialogTitle>Release Payment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormLabel>Payment Status</FormLabel>
              <TextField
                select
                fullWidth
                size="small"
                value={releasePaymentData.payment_complete_status}
                onChange={(e) => setReleasePaymentData(prev => ({ ...prev, payment_complete_status: e.target.value }))}
                SelectProps={{ native: true }}
              >
                <option value="" disabled>Select Payment Status</option>
                {paymentStatus.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <FormLabel>Wire #</FormLabel>
              <TextField
                fullWidth
                value={releasePaymentData.wire_ref}
                onChange={(e) =>
                  setReleasePaymentData(prev => ({ ...prev, wire_ref: e.target.value }))
                }
                size="small"
                disabled={releasePaymentData.payment_complete_status != 'paid'}
              />
            </Grid>
            <Grid item xs={12}>
              <FormLabel>Payment Date</FormLabel>
              <TextField
                fullWidth
                type="date"
                value={releasePaymentData.payment_date}
                onChange={(e) =>
                  setReleasePaymentData(prev => ({ ...prev, payment_date: e.target.value }))
                }
                size="small"
                disabled={releasePaymentData.payment_complete_status != 'paid'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <CommonButton
            text="Cancel"
            handleClick={handleCloseReleaseModal}
            hoverColor="#EE3F00"
          />
          <CommonButton
            text="Submit"
            handleClick={handleReleasePaymentSubmit}
            hoverColor="#EE3F00"
            disabled={isButtonEnable}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CoversheetList;
